import { Component, Input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NgxMaskDirective } from 'ngx-mask';
import { PhoneFormatPipe } from 'src/app/shared/pipes/phone-format.pipe';
import { toNationalDigits } from 'src/app/shared/utils/format.utils';
import { SponsorsService } from '../../../../core/services/sponsors.service';
import {
  SponsorLocation,
  SponsorLocationCreateRequest,
  SponsorLocationUpdateRequest
} from '../../../../core/models/sponsor.model';
import { UsStateSelect } from 'src/app/shared/components/app-us-state-select/app-us-state-select';

/**
 * Sponsor Locations Component
 *
 * Standalone child component for managing sponsor locations.
 * Displays a table with inline add/edit forms following the
 * point rates CRUD pattern.
 */
@Component({
  selector: 'app-sponsor-locations',
  standalone: true,
  imports: [CommonModule, FormsModule, NgxMaskDirective, PhoneFormatPipe, UsStateSelect],
  templateUrl: './sponsor-locations.component.html',
  styleUrl: './sponsor-locations.component.scss'
})
export class SponsorLocationsComponent implements OnInit {
  @Input() sponsorId = '';
  @Input() contactType = '';

  locations: SponsorLocation[] = [];
  loading = true;
  errorMessage = '';
  successMessage = '';

  // Add form state
  showAddForm = false;
  addingLocation = false;
  addFormError = '';
  newLocation: SponsorLocationCreateRequest = {
    name: '',
    street1: '',
    street2: null,
    city: '',
    state: '',
    zipCode: '',
    latitude: null,
    longitude: null,
    phone: null,
    email: null
  };

  // Edit state
  editingLocationId: string | null = null;
  editData: SponsorLocationUpdateRequest = {};
  editError = '';
  savingEdit = false;

  constructor(private sponsorsService: SponsorsService) {}

  ngOnInit(): void {
    if (this.sponsorId) {
      this.loadLocations();
    }
  }

  /**
   * Whether the contact type excludes locations
   */
  get isDisabled(): boolean {
    return this.contactType === 'reps_only' || this.contactType === 'contact_form_only';
  }

  /**
   * Informational message when locations are not applicable
   */
  get infoMessage(): string {
    if (this.contactType === 'reps_only') {
      return 'This sponsor uses representatives only. Change the contact type on the Info tab to manage locations.';
    }
    if (this.contactType === 'contact_form_only') {
      return 'This sponsor uses a contact form only. Change the contact type on the Info tab to manage locations.';
    }
    return '';
  }

  loadLocations(): void {
    this.loading = true;
    this.errorMessage = '';

    this.sponsorsService.listSponsorLocations(this.sponsorId).subscribe({
      next: (locations) => {
        this.locations = locations;
        this.loading = false;
      },
      error: (err) => {
        console.error('Failed to load locations:', err);
        this.errorMessage = 'Failed to load locations';
        this.loading = false;
      }
    });
  }

  // ============================================
  // ADD LOCATION
  // ============================================

  toggleAddForm(): void {
    this.showAddForm = !this.showAddForm;
    this.addFormError = '';
    if (this.showAddForm) {
      this.resetNewLocationForm();
    }
  }

  resetNewLocationForm(): void {
    this.newLocation = {
      name: '',
      street1: '',
      street2: null,
      city: '',
      state: '',
      zipCode: '',
      latitude: null,
      longitude: null,
      phone: null,
      email: null
    };
  }

  submitLocation(): void {
    this.addFormError = '';

    if (!this.newLocation.name?.trim()) {
      this.addFormError = 'Location name is required';
      return;
    }
    if (!this.newLocation.street1?.trim()) {
      this.addFormError = 'Street address is required';
      return;
    }
    if (!this.newLocation.city?.trim()) {
      this.addFormError = 'City is required';
      return;
    }
    if (!this.newLocation.state?.trim()) {
      this.addFormError = 'State is required';
      return;
    }
    if (!this.newLocation.zipCode?.trim()) {
      this.addFormError = 'Zip code is required';
      return;
    }

    this.addingLocation = true;

    const request: SponsorLocationCreateRequest = {
      name: this.newLocation.name.trim(),
      street1: this.newLocation.street1.trim(),
      city: this.newLocation.city.trim(),
      state: this.newLocation.state.trim(),
      zipCode: this.newLocation.zipCode.trim()
    };

    if (this.newLocation.street2?.trim()) {
      request.street2 = this.newLocation.street2.trim();
    }
    if (this.newLocation.phone?.trim()) {
      request.phone = this.newLocation.phone.trim();
    }
    if (this.newLocation.email?.trim()) {
      request.email = this.newLocation.email.trim();
    }
    if (this.newLocation.latitude != null && !isNaN(this.newLocation.latitude)) {
      request.latitude = this.newLocation.latitude;
    }
    if (this.newLocation.longitude != null && !isNaN(this.newLocation.longitude)) {
      request.longitude = this.newLocation.longitude;
    }

    this.sponsorsService.createSponsorLocation(this.sponsorId, request).subscribe({
      next: () => {
        this.showAddForm = false;
        this.addingLocation = false;
        this.showSuccess('Location created successfully');
        this.loadLocations();
      },
      error: (err) => {
        console.error('Failed to create location:', err);
        this.addFormError = err.message || 'Failed to create location';
        this.addingLocation = false;
      }
    });
  }

  // ============================================
  // EDIT LOCATION
  // ============================================

  startEdit(location: SponsorLocation): void {
    this.editingLocationId = location.id;
    this.editError = '';
    this.editData = {
      name: location.name,
      street1: location.street1,
      street2: location.street2,
      city: location.city,
      state: location.state,
      zipCode: location.zipCode,
      phone: toNationalDigits(location.phone),
      email: location.email,
      latitude: location.latitude,
      longitude: location.longitude
    };
  }

  cancelEdit(): void {
    this.editingLocationId = null;
    this.editData = {};
    this.editError = '';
  }

  saveEdit(): void {
    if (!this.editingLocationId) return;

    this.editError = '';

    if (this.editData.name !== undefined && !this.editData.name?.trim()) {
      this.editError = 'Location name cannot be empty';
      return;
    }
    if (this.editData.street1 !== undefined && !this.editData.street1?.trim()) {
      this.editError = 'Street address cannot be empty';
      return;
    }
    if (this.editData.city !== undefined && !this.editData.city?.trim()) {
      this.editError = 'City cannot be empty';
      return;
    }
    if (this.editData.state !== undefined && !this.editData.state?.trim()) {
      this.editError = 'State cannot be empty';
      return;
    }
    if (this.editData.zipCode !== undefined && !this.editData.zipCode?.trim()) {
      this.editError = 'Zip code cannot be empty';
      return;
    }

    this.savingEdit = true;

    this.sponsorsService.updateSponsorLocation(this.sponsorId, this.editingLocationId, this.editData).subscribe({
      next: () => {
        this.editingLocationId = null;
        this.editData = {};
        this.savingEdit = false;
        this.showSuccess('Location updated successfully');
        this.loadLocations();
      },
      error: (err) => {
        console.error('Failed to update location:', err);
        this.editError = err.message || 'Failed to update location';
        this.savingEdit = false;
      }
    });
  }

  // ============================================
  // ARCHIVE LOCATION
  // ============================================

  archiveLocation(location: SponsorLocation): void {
    if (!confirm(`Are you sure you want to archive "${location.name}"?`)) {
      return;
    }

    this.sponsorsService.archiveSponsorLocation(this.sponsorId, location.id).subscribe({
      next: () => {
        this.showSuccess('Location archived successfully');
        this.loadLocations();
      },
      error: (err) => {
        console.error('Failed to archive location:', err);
        this.errorMessage = 'Failed to archive location';
      }
    });
  }

  // ============================================
  // UTILITY
  // ============================================

  formatAddress(location: SponsorLocation): string {
    const parts = [location.street1];
    if (location.street2) {
      parts.push(location.street2);
    }
    parts.push(`${location.city}, ${location.state} ${location.zipCode}`);
    return parts.join(', ');
  }

  showSuccess(message: string): void {
    this.successMessage = message;
    setTimeout(() => {
      this.successMessage = '';
    }, 4000);
  }
}
